import { useTranslation } from 'react-i18next';
import { useContext, memo } from 'react';
import { Form } from 'formik';
import Spacer from 'components/Shared/Spacer';
import { LargeButton, ButtonState } from 'components/Shared/Buttons';
import css from 'styled-jsx/css';
import { screen, colors } from 'components/styles/variables';
import useEvents from 'lib/hooks/useEvents';
import VdpEventContext from 'contexts/VdpEventContext';
import tapEvent from 'lib/Tracking/LeadForm/TapEvent';
import Terms from 'components/LeadForm/Terms';
import classNames from 'classnames';
import LeadFormTrackingContext from 'components/Vdp/LeadFormTrackingContext';
import { LeadSubmission } from 'lib/hooks/useSubmitted';
import styles, { modalFormStyles } from './styles';
import { SubmissionState } from './FormManager';
import FieldSet from './FieldSet';

interface LeadFormProps {
  showZipCode?: boolean;
  showAddress?: boolean;
  showMessage?: boolean;
  showAlerts?: boolean;
  showTerms?: boolean;
  lockEmail?: boolean;
  isSubmitted: boolean;
  submissionState: SubmissionState;
  submitButtonClassName?: string;
  submitButtonNormalText?: string;
  marginClassName?: string;
  inputClassName?: string;
  leadSubmission: LeadSubmission;
  vehiclePrice?: number;
  listingId: number;
}

const desktopButtonCss = css.resolve`
  display: none;

  @media (min-width: ${screen.smMin}) {
    display: block;
  }
`;

const MemoizedFieldSet = memo(FieldSet);

export default function FormComponent({
  showZipCode,
  showAddress,
  showMessage = true,
  showAlerts = true,
  showTerms = true,
  lockEmail = false,
  isSubmitted,
  submissionState,
  submitButtonNormalText,
  marginClassName = '',
  inputClassName = '',
  leadSubmission,
  submitButtonClassName,
  vehiclePrice,
  listingId,
}: LeadFormProps): JSX.Element {
  const eventData = useContext(LeadFormTrackingContext);
  const { t } = useTranslation();

  function submitButton(): { state: ButtonState; text?: string } {
    switch (submissionState) {
      case SubmissionState.disabled:
        return { state: ButtonState.disabled, text: t('leadForm.sendRequest') };
      case SubmissionState.submitted:
        return { state: ButtonState.disabled, text: t('leadForm.contacted') };
      case SubmissionState.submitting:
        return { state: ButtonState.thinking, text: t('leadForm.contacted') };
      default:
        return { state: ButtonState.normal, text: t('leadForm.contacted') };
    }
  }

  const logger = useEvents();

  const { trackingParams } = useContext(VdpEventContext);
  return (
    <>
      <Form
        data-testid="lead-form-modal"
        className={`form ${modalFormStyles.className}`}
        noValidate={true}
      >
        <Spacer size="29px" />
        <MemoizedFieldSet
          leadSubmission={leadSubmission}
          showMessage={showMessage}
          showAlerts={showAlerts}
          showZipCode={showZipCode}
          showAddress={showAddress}
          lockEmail={lockEmail}
          isSubmitted={isSubmitted}
          marginClassName={marginClassName}
          formInputClassName={inputClassName}
          vehiclePrice={vehiclePrice}
          listingId={listingId}
        />
        <div className="send-request-container">
          <LargeButton
            normalText={submitButtonNormalText || t('leadForm.sendRequest')}
            data-testid="submit"
            disabledText={submitButton().text}
            className={classNames(
              desktopButtonCss.className,
              submitButtonClassName,
              'submit-button',
              'desktop-button'
            )}
            state={submitButton().state}
            type="submit"
            onClick={(): void => {
              logger.log(tapEvent({ action: 'lead_form_submit', trackingParams, ...eventData }));
            }}
          />
        </div>
        {showTerms && (
          <>
            <Spacer size="8px" smSize="16px" />
            <Terms termsClassName="padding" />
          </>
        )}
        <div className="submit-button-container mobile">
          <LargeButton
            normalText={submitButtonNormalText || t('leadForm.sendRequest')}
            disabledText={submitButton().text}
            className={classNames(submitButtonClassName, 'submit-button')}
            state={submitButton().state}
            type="submit"
            onClick={(): void => {
              logger.log(tapEvent({ action: 'lead_form_submit', trackingParams, ...eventData }));
            }}
          />
        </div>
      </Form>
      <style jsx>{styles}</style>
      {desktopButtonCss.styles}
      {modalFormStyles.styles}
      <style jsx>{`
        .submit-button-container {
          position: sticky;
          margin-top: auto;
          background: ${colors.WHITE};
          bottom: 0;
          width: 100%;
          height: 76px;
          padding: 16px;
          border-top: 1px solid ${colors.G2};
          display: block;
        }

        @media (min-width: ${screen.smMin}) {
          .submit-button-container {
            position: relative;
            border: none;
          }
        }

        @media (min-width: ${screen.smMin}) {
          .submit-button-container {
            display: none;
          }
        }
        @media (min-width: ${screen.smMin}) {
          .send-request-container {
            padding: 0px 16px;
            margin-top: 32px;
          }
        }
      `}</style>
    </>
  );
}
